import React, { useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { CustomizationOptions } from '../../types/customization';

interface SurfaceProps {
  options: CustomizationOptions;
  material: THREE.Material;
  onEdgeSelect?: () => void;
}

export const Surface: React.FC<SurfaceProps> = ({ options, material, onEdgeSelect }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  const geometry = useMemo(() => {
    const { width, length, thickness, shape: shapeType, edgeProfile } = options;
    const w = width / 1000;
    const l = length / 1000;
    const t = thickness / 1000;

    // Create shape based on type
    const shape = new THREE.Shape();

    switch (shapeType) {
      case 'round': {
        const radius = Math.min(w, l) / 2;
        const segments = 180;
        const controlPointOffset = radius * 0.552284;

        shape.moveTo(radius, 0);
        shape.bezierCurveTo(
          radius, controlPointOffset,
          controlPointOffset, radius,
          0, radius
        );
        shape.bezierCurveTo(
          -controlPointOffset, radius,
          -radius, controlPointOffset,
          -radius, 0
        );
        shape.bezierCurveTo(
          -radius, -controlPointOffset,
          -controlPointOffset, -radius,
          0, -radius
        );
        shape.bezierCurveTo(
          controlPointOffset, -radius,
          radius, -controlPointOffset,
          radius, 0
        );
        break;
      }

      case 'oval': {
        const radiusX = w / 2;
        const radiusY = l / 2;
        const controlPointX = radiusX * 0.5519;
        const controlPointY = radiusY * 0.5519;

        shape.moveTo(-radiusX, 0);
        shape.bezierCurveTo(
          -radiusX, controlPointY,
          -controlPointX, radiusY,
          0, radiusY
        );
        shape.bezierCurveTo(
          controlPointX, radiusY,
          radiusX, controlPointY,
          radiusX, 0
        );
        shape.bezierCurveTo(
          radiusX, -controlPointY,
          controlPointX, -radiusY,
          0, -radiusY
        );
        shape.bezierCurveTo(
          -controlPointX, -radiusY,
          -radiusX, -controlPointY,
          -radiusX, 0
        );
        break;
      }

      case 'racetrack': {
        const straightLength = l - w;
        const radius = w / 2;

        shape.moveTo(radius, -straightLength / 2);
        shape.absarc(0, -straightLength / 2, radius, 0, Math.PI, true);
        shape.lineTo(-radius, straightLength / 2);
        shape.absarc(0, straightLength / 2, radius, Math.PI, 0, true);
        shape.lineTo(radius, -straightLength / 2);
        break;
      }

      case 'danish-oval': {
        const radiusX = w / 2;
        const radiusY = l / 2;
        const controlPointX = radiusX * 0.552284;
        const controlPointY = radiusY * 0.552284;
        
        shape.moveTo(-radiusX, 0);
        shape.bezierCurveTo(
          -radiusX, controlPointY,
          -controlPointX, radiusY,
          0, radiusY
        );
        shape.bezierCurveTo(
          controlPointX, radiusY,
          radiusX, controlPointY,
          radiusX, 0
        );
        shape.bezierCurveTo(
          radiusX, -controlPointY,
          controlPointX, -radiusY,
          0, -radiusY
        );
        shape.bezierCurveTo(
          -controlPointX, -radiusY,
          -radiusX, -controlPointY,
          -radiusX, 0
        );
        break;
      }

      case 'kidney': {
        const radiusX = w / 2;
        const radiusY = l / 2;
        const indentDepth = radiusX * 0.2;
        const indentWidth = radiusY * 0.3;
        const segments = 180;

        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          let x = Math.cos(angle) * radiusX;
          let y = Math.sin(angle) * radiusY;

          if (x > 0) {
            const indentFactor = Math.exp(-Math.pow(y / indentWidth, 2));
            x -= indentDepth * indentFactor;
          }

          if (i === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        }
        break;
      }

      case 'super-elliptical': {
        // Super-elliptical shape (Piet Hein's superellipse)
        const radiusX = w / 2;
        const radiusY = l / 2;
        const n = 2.5; // Superellipse parameter (2.5 gives a nice rounded rectangle)
        const segments = 180;

        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          // Superellipse formula
          const x = Math.sign(Math.cos(angle)) * Math.pow(Math.abs(Math.cos(angle)), 2/n) * radiusX;
          const y = Math.sign(Math.sin(angle)) * Math.pow(Math.abs(Math.sin(angle)), 2/n) * radiusY;
          
          if (i === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        }
        break;
      }

      case 'boat-shaped': {
        // Boat-shaped table with tapered ends
        const radiusX = w / 2;
        const radiusY = l / 2;
        const taperAmount = radiusX * 0.3; // How much to taper the ends
        const segments = 180;

        for (let i = 0; i <= segments; i++) {
          const t = i / segments;
          const angle = t * Math.PI * 2;
          
          // Apply tapering at the ends (along the length)
          const taper = Math.sin(t * Math.PI) * taperAmount;
          const x = Math.cos(angle) * (radiusX - taper);
          const y = Math.sin(angle) * radiusY;

          if (i === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        }
        break;
      }

      case 'conference': {
        // Conference table with subtle curved sides
        const radiusX = w / 2;
        const radiusY = l / 2;
        const curveAmount = radiusX * 0.1; // Subtle curve on the sides
        const segments = 180;

        for (let i = 0; i <= segments; i++) {
          const t = i / segments;
          const angle = t * Math.PI * 2;
          
          // Add subtle curves to the sides
          const curve = Math.sin(t * Math.PI * 2) * curveAmount;
          const x = Math.cos(angle) * (radiusX + curve);
          const y = Math.sin(angle) * radiusY;

          if (i === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        }
        break;
      }

      default: {
        // Rectangular shape
        shape.moveTo(-w/2, -l/2);
        shape.lineTo(w/2, -l/2);
        shape.lineTo(w/2, l/2);
        shape.lineTo(-w/2, l/2);
        shape.lineTo(-w/2, -l/2);
      }
    }

    // Create extrude settings based on edge profile
    const getEdgeSettings = () => {
      const baseSettings = {
        depth: t,
        bevelEnabled: true,
        bevelSegments: 8
      };

      switch (edgeProfile) {
        case 'eased':
          return {
            ...baseSettings,
            bevelThickness: t * 0.1,
            bevelSize: t * 0.1,
            bevelSegments: 12
          };
        case 'beveled':
          return {
            ...baseSettings,
            bevelThickness: t * 0.15,
            bevelSize: t * 0.15,
            bevelSegments: 1
          };
        case 'double-beveled':
          return {
            ...baseSettings,
            bevelThickness: t * 0.2,
            bevelSize: t * 0.2,
            bevelSegments: 2
          };
        case 'mitered':
          return {
            ...baseSettings,
            bevelThickness: t * 0.5,
            bevelSize: t * 0.5,
            bevelSegments: 1
          };
        case 'sharknose':
          return {
            ...baseSettings,
            bevelThickness: t * 0.3,
            bevelSize: t * 0.3,
            bevelSegments: 4,
            bevelOffset: -t * 0.1
          };
        default:
          return {
            ...baseSettings,
            bevelEnabled: false
          };
      }
    };

    return new THREE.ExtrudeGeometry(shape, getEdgeSettings());
  }, [options]);

  // Calculate the height offset to position the shape above the ground
  const heightOffset = useMemo(() => {
    return options.thickness / 2000; // Half thickness to center the object
  }, [options.thickness]);

  return (
    <group position={[0, heightOffset, 0]}>
      {/* Main surface mesh */}
      <mesh
        ref={meshRef}
        geometry={geometry}
        material={material}
        castShadow
        receiveShadow
        onClick={(e) => {
          if (e.face && onEdgeSelect) {
            const normal = e.face.normal;
            if (Math.abs(normal.y) < 0.5) {
              onEdgeSelect();
            }
          }
        }}
      />
    </group>
  );
};